import React, {
  createContext,
  useState,
  useContext,
  useCallback,
  useMemo,
} from "react";

export const AdminContext = createContext();

const AdminProvider = ({ children }) => {
  const [adminToken, setAdminToken] = useState(
    localStorage.getItem("token") || null
  );
  const [adminProfile, setAdminProfile] = useState(null);

  const login = useCallback((token) => {
    setAdminToken(token);
    localStorage.setItem("token", token);
  }, []);

  const logout = useCallback(() => {
    setAdminToken(null);
    setAdminProfile(null);
    localStorage.removeItem("token");
  }, []);

  const updateProfile = useCallback((profileData) => {
    setAdminProfile(profileData);
  }, []);

  const hasPermission = useCallback(
    (permission) => {
      if (!adminProfile) return false;
      // Super-admin has access to everything
      if (adminProfile.adminType === "super-admin") {
        return true;
      }
      // Sub-admin access depends on permissions
      if (adminProfile.adminType === "sub-admin" && adminProfile.permissions) {
        return adminProfile.permissions[permission] === true;
      }
      return false;
    },
    [adminProfile]
  );

  const isLoggedIn = useCallback(() => {
    return !!adminToken;
  }, [adminToken]);

  // Memoize context value to prevent unnecessary re-renders
  const providerValue = useMemo(
    () => ({
      adminToken,
      adminProfile,
      login,
      logout,
      updateProfile,
      hasPermission,
      isLoggedIn,
    }),
    [
      adminToken,
      adminProfile,
      login,
      logout,
      updateProfile,
      hasPermission,
      isLoggedIn,
    ]
  );
  return (
    <AdminContext.Provider value={providerValue}>
      {children}
    </AdminContext.Provider>
  );
};

// Custom hook to use the admin context
export const useAdmin = () => {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error("useAdmin must be used within an AdminProvider");
  }
  return context;
};

export default AdminProvider;
